import { IApiLineupPlayer } from '@trackman/baseball-shared-components';
import { Button } from '@trackman/web-shared-components';
import { ServiceTypes } from 'constants/ServiceTypes';
import { Ruleset } from 'enums/Ruleset';
import { ApiLineupDraggablePlayer } from 'interfaces/team/ApiLineupDraggablePlayer';
import { IPlayer } from 'interfaces/team/IPlayer';
import { ITeam } from 'interfaces/team/ITeam';
import { observer } from 'mobx-react-lite';
import { LineupManager } from 'models/setLineup/LineupManager';
import StrikeZoneMissing from 'pages/SetLineup/components/StrikeZoneMissing/StrikeZoneMissing';
import { TeamSetup } from 'pages/SetLineup/components/TeamSetup/TeamSetup';
import React, { useEffect, useState } from 'react';
import { LogService } from 'services/data-services/LogService';
import { IFeatureConfigService } from 'services/FeatureConfigService';
import { apiLineupPlayerToLineupEntryMap } from 'services/helper-services/mappingHelpers/playerMappings';
import { useInjection } from 'services/ioc/ioc.react';
import RulesetService from 'services/RulesetService';
import { LineupStore } from 'stores/LineupStore';
import { NotificationStore } from 'stores/NotificationStore';
import { SessionStore } from 'stores/SessionStore';
import { Editor, Footer, FooterButtons, StyledButton, StyledModal } from './EditLineupModalStyles';

interface IProps {
  isVisible: boolean;
  roster: IPlayer[];
  ruleset: Ruleset;
  team: ITeam | null;
  currentLineup: IApiLineupPlayer[];
  changeLineupAsync: (newLineup: ApiLineupDraggablePlayer[]) => Promise<void>;
  onDismiss: () => void;
  unavailablePlayerIds: string[];
}

const EditLineupModal: React.FC<IProps> = observer((props: IProps) => {
  const featureConfigService = useInjection<IFeatureConfigService>(ServiceTypes.FeatureConfigService);
  const { isVisible, roster, team, currentLineup, onDismiss, changeLineupAsync, ruleset, unavailablePlayerIds } = props;
  const logService = useInjection<LogService>(ServiceTypes.LogService);
  const rulesetService = useInjection<RulesetService>(ServiceTypes.RulesetService);
  const lineupStore = useInjection<LineupStore>(ServiceTypes.LineupStore);
  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);
  const [lineupManager, setLineupManager] = useState<LineupManager>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const notificationStore = useInjection<NotificationStore>(ServiceTypes.NotificationStore);

  useEffect(() => {
    if (isVisible && team) {
      const activeStrikeZone =
        lineupStore.overridedStrikeZone?.activeStrikeZone ||
        sessionStore.currentSessionMetadata.strikeZone?.customDefinition?.activeStrikeZone ||
        sessionStore.currentSessionMetadata.strikeZone?.activeStrikeZone;

      setLineupManager(
        new LineupManager(
          team,
          ruleset,
          roster,
          activeStrikeZone,
          rulesetService.getPlayerLimit(ruleset),
          currentLineup.map(apiLineupPlayerToLineupEntryMap)
        )
      );
    }
  }, [isVisible, team, currentLineup, roster, ruleset]);

  const updateLineup = async () => {
    try {
      if (!lineupManager) {
        throw Error('[Edit lineup modal] Lineup manager is empty.');
      }
      setIsLoading(true);
      changeLineupAsync(lineupManager.getMetadataLineup());
    } catch (error) {
      notificationStore.addNotification({
        id: 'lineup-change-error',
        message: 'There was an error while changing lineup.',
        variant: 'error',
        removable: true,
        onClose: () => {
          notificationStore.removeNotification('lineup-change-error');
        },
      });
      console.log(error);
      logService.logError(error, {
        function: 'EditLineupModal@updateLineup',
      });
    } finally {
      setIsLoading(false);
    }
  };
  if (!isVisible) {
    return null;
  }
  return (
    <StyledModal open={isVisible} onClose={onDismiss} title={`Edit ${team?.name ?? ''}`} titleCenter>
      <Editor>
        <TeamSetup
          isPlaceholder={!lineupManager}
          lineupManager={lineupManager}
          unavailablePlayerIds={unavailablePlayerIds}
          isLoading={false}
          size='sm'
          containerClass='div[class*="-modalBody-"]'
        />
      </Editor>
      <Footer justifyContent={featureConfigService.getConfig().isRoboUmpEnabled ? 'space-between' : 'flex-end'}>
        {featureConfigService.getConfig().isRoboUmpEnabled && (lineupManager?.playersWithMissingSZ || []).length > 0 ? (
          <StrikeZoneMissing players={lineupManager?.playersWithMissingSZ || []} />
        ) : (
          <div></div>
        )}
        <FooterButtons>
          <StyledButton intent={'secondary'} label={'Cancel'} onClick={onDismiss} width={'85px'} />
          <Button
            intent={'primary'}
            label={'Confirm'}
            onClick={updateLineup}
            width={'100px'}
            isLoading={isLoading}
            disabled={!lineupManager || !lineupManager.isValidated}
          />
        </FooterButtons>
      </Footer>
    </StyledModal>
  );
});

export default EditLineupModal;
